import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Trash2, Plus, X, Save, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Editor } from '@tinymce/tinymce-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TINYMCE_KEY = process.env.REACT_APP_TINYMCE_KEY || 'no-api-key';

const empty = { city: '', regulation_id: '', region: '', county: '', content_html: '', content_html_zh: '', image: '', sort_order: 0 };

const PreApprovedManager = ({ isZh = false, token }) => {
  const [items, setItems] = useState([]);
  const [regulations, setRegulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const editorRef = useRef(null);
  const editorZhRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [pa, regs] = await Promise.all([
        axios.get(`${API}/admin/pre-approved`),
        axios.get(`${API}/admin/regulations`),
      ]);
      setItems(pa.data?.items || []);
      setRegulations(regs.data?.regulations || []);
    } catch {
      toast.error(isZh ? '加载失败' : 'Load failed');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => setEditing({ ...empty });
  const startEdit = (i) => setEditing({ ...i });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.city) { toast.error(isZh ? '请填写城市名' : 'City name required'); return; }
    try {
      const payload = {
        ...editing,
        content_html: editorRef.current?.getContent?.() ?? editing.content_html,
        content_html_zh: editorZhRef.current?.getContent?.() ?? editing.content_html_zh,
      };
      if (editing.id) {
        await axios.put(`${API}/admin/pre-approved/${editing.id}`, payload, { headers });
        toast.success(isZh ? '已保存' : 'Saved');
      } else {
        await axios.post(`${API}/admin/pre-approved`, payload, { headers });
        toast.success(isZh ? '已创建' : 'Created');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isZh ? '保存失败' : 'Save failed'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(isZh ? '确认删除？' : 'Delete this entry?')) return;
    try {
      await axios.delete(`${API}/admin/pre-approved/${id}`, { headers });
      toast.success(isZh ? '已删除' : 'Deleted');
      load();
    } catch { toast.error(isZh ? '删除失败' : 'Delete failed'); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/upload`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url || r.data?.path || '';
      if (url) {
        setEditing({ ...editing, image: url });
        toast.success(isZh ? '上传成功' : 'Uploaded');
      }
    } catch { toast.error(isZh ? '上传失败' : 'Upload failed'); }
    finally { setUploading(false); }
  };

  // ---------- LIST VIEW ----------
  if (!editing) {
    return (
      <div data-testid="pre-approved-manager">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">{isZh ? 'Pre-Approved 方案管理' : 'Pre-Approved Plans'}</h2>
          <Button onClick={startNew} className="bg-red-600 hover:bg-red-700 text-white" data-testid="pa-new-btn">
            <Plus className="w-4 h-4 mr-1" />{isZh ? '新建' : 'New'}
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />{isZh ? '加载中...' : 'Loading...'}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">{isZh ? '暂无数据，点击新建' : 'No entries yet. Click "New" to add.'}</div>
        ) : (
          <div className="grid gap-3">
            {items.map(it => {
              const linkedReg = regulations.find(r => r.id === it.regulation_id);
              return (
                <Card key={it.id} className="hover:shadow-md transition" data-testid={`pa-row-${it.city}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    {it.image ? (
                      <img src={it.image} alt="" className="w-20 h-20 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-20 h-20 rounded bg-red-50 flex items-center justify-center text-red-400 flex-shrink-0"><ImageIcon className="w-6 h-6" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-lg">{it.city}</div>
                      <div className="text-sm text-gray-600 flex flex-wrap gap-2 mt-1">
                        {it.region && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs">{it.region}</span>}
                        {it.county && <span>{it.county}</span>}
                        {linkedReg && <span className="text-blue-600">→ {linkedReg.city} {isZh ? '法规' : 'Regulations'}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => startEdit(it)}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => remove(it.id)} className="text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ---------- EDIT VIEW ----------
  return (
    <div data-testid="pa-editor">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">{editing.id ? (isZh ? '编辑方案' : 'Edit Plan') : (isZh ? '新建方案' : 'New Plan')}</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={cancel}><X className="w-4 h-4 mr-1" />{isZh ? '取消' : 'Cancel'}</Button>
          <Button onClick={save} className="bg-red-600 hover:bg-red-700 text-white" data-testid="pa-save-btn"><Save className="w-4 h-4 mr-1" />{isZh ? '保存' : 'Save'}</Button>
        </div>
      </div>
      <Card><CardContent className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-1">{isZh ? '城市名' : 'City'} *</label>
            <Input value={editing.city} onChange={e => setEditing({ ...editing, city: e.target.value })} placeholder="Pasadena" data-testid="pa-city-input" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">{isZh ? '区域' : 'Region'}</label>
            <select value={editing.region} onChange={e => setEditing({ ...editing, region: e.target.value })} className="border border-gray-300 rounded px-3 py-2 w-full" data-testid="pa-region-select">
              <option value="">{isZh ? '请选择...' : 'Select...'}</option>
              <option value="North">North</option>
              <option value="South">South</option>
              <option value="San Diego">San Diego</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">{isZh ? '县' : 'County'}</label>
            <Input value={editing.county} onChange={e => setEditing({ ...editing, county: e.target.value })} placeholder="Los Angeles County" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">{isZh ? '关联 ADU 法规' : 'Linked ADU Regulation'}</label>
            <select value={editing.regulation_id} onChange={e => setEditing({ ...editing, regulation_id: e.target.value })} className="border border-gray-300 rounded px-3 py-2 w-full" data-testid="pa-regulation-select">
              <option value="">{isZh ? '不关联' : 'None'}</option>
              {regulations.map(r => <option key={r.id} value={r.id}>{r.city}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">{isZh ? '封面图' : 'Cover Image'}</label>
          {editing.image && <img src={editing.image} alt="" className="w-48 rounded mb-2" />}
          <div className="flex gap-2">
            <Input value={editing.image} onChange={e => setEditing({ ...editing, image: e.target.value })} placeholder="https://..." />
            <input type="file" accept="image/*" ref={fileRef} onChange={uploadImage} className="hidden" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
              <span className="ml-1">{isZh ? '上传' : 'Upload'}</span>
            </Button>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">{isZh ? '内容 HTML（英文）' : 'Content HTML (English)'}</label>
          <Editor
            apiKey={TINYMCE_KEY}
            onInit={(_e, editor) => { editorRef.current = editor; }}
            initialValue={editing.content_html || ''}
            init={{
              height: 500, menubar: false,
              plugins: 'lists link image media table code preview wordcount autolink',
              toolbar: 'undo redo | blocks | bold italic underline | alignleft aligncenter alignright | bullist numlist | link image media table | code preview',
              branding: false, promotion: false,
              convert_urls: false,
              relative_urls: false,
              remove_script_host: false,
            }}
          />
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">{isZh ? '内容 HTML（中文）— 留空时显示英文' : 'Content HTML (Chinese) — falls back to English if empty'}</label>
          <Editor
            apiKey={TINYMCE_KEY}
            onInit={(_e, editor) => { editorZhRef.current = editor; }}
            initialValue={editing.content_html_zh || ''}
            init={{
              height: 500, menubar: false,
              plugins: 'lists link image media table code preview wordcount autolink',
              toolbar: 'undo redo | blocks | bold italic underline | alignleft aligncenter alignright | bullist numlist | link image media table | code preview',
              branding: false, promotion: false,
              convert_urls: false,
              relative_urls: false,
              remove_script_host: false,
            }}
          />
        </div>
      </CardContent></Card>
    </div>
  );
};

export default PreApprovedManager;
